import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import { funnelText } from "@/lib/funnel-planner/i18n";
import { conciseCrawlerHeading } from "@/lib/seo/headings";

const STEP_TITLE_KEYS = [
  "step1Title", "step2Title", "step3Title", "step4Title", "step5Title", "step6Title", "step7Title",
] as const;

const STEP_SUBTITLE_KEYS = [
  "step1Subtitle", "step2Subtitle", "step3Subtitle", "step4Subtitle", "step5Subtitle", "step6Subtitle", "step7Subtitle",
] as const;

const STEP_NAME_KEYS = [
  "stepNameTemplate", "stepNameTraffic", "stepNameProducts", "stepNameAudience", "stepNameFunnel", "stepNameProjections", "stepNameDownload",
] as const;

export function StepHeader({
  auditTicket,
  step,
  totalSteps,
  content,
  headingLevel = "h2",
  readinessSurface,
}: {
  auditTicket?: string;
  step: number;
  totalSteps: number;
  content: FunnelPlannerContent;
  headingLevel?: "h1" | "h2";
  readinessSurface?: {
    readonly blockersBucket: string;
    readonly currentState: string;
    readonly liveLabel: string;
    readonly nextLabel: string;
    readonly progressLabel: string;
    readonly readyCount: number;
    readonly supportedStates: string;
    readonly tier: string;
    readonly totalCount: number;
  };
}) {
  const titleKey = STEP_TITLE_KEYS[step - 1];
  const subtitleKey = STEP_SUBTITLE_KEYS[step - 1];
  const stepNames = STEP_NAME_KEYS.map((key) => funnelText(content, key, key));
  const currentStepName = stepNames[step - 1] ?? "";
  const title = titleKey ? conciseCrawlerHeading(content[titleKey], currentStepName) : "";

  return (
    <div
      className="funnel-planner-step-header"
      data-audit-ticket={auditTicket}
      data-funnel-planner-step-header="true"
      data-blockers-bucket={readinessSurface?.blockersBucket}
      data-gamification-current-state={readinessSurface?.currentState}
      data-gamification-reduced-motion-static={readinessSurface ? "supported" : undefined}
      data-gamification-supported-states={readinessSurface?.supportedStates}
      data-readiness-ready-count={readinessSurface?.readyCount}
      data-readiness-tier={readinessSurface?.tier}
      data-readiness-total-count={readinessSurface?.totalCount}
      data-tier-one-family={readinessSurface ? "readiness_board" : undefined}
    >
      {readinessSurface ? (
        <span className="sr-only" role="status" aria-live="polite">
          {readinessSurface.liveLabel}
        </span>
      ) : null}
      <div
        style={{ display: "flex", gap: 4 }}
        role="progressbar"
        aria-valuenow={step}
        aria-valuemin={1}
        aria-valuemax={totalSteps}
        aria-label={funnelText(content, "progressAriaLabel", "Funnel planner progress")}
      >
        {Array.from({ length: totalSteps }, (_, i) => (
          <div
            key={i}
            style={{
              height: 6,
              flex: 1,
              borderRadius: 9999,
              transition: "background-color 0.2s",
              backgroundColor: i + 1 < step ? "#2563eb" : i + 1 === step ? "#3b82f6" : "#1e293b",
            }}
          />
        ))}
      </div>

      <p style={{ marginTop: 12, fontSize: 12, fontWeight: 500, color: "#94a3b8" }}>
        {funnelText(content, "stepProgressLabel", "Step {step} of {total} - about 3 minutes", {
          step,
          total: totalSteps,
        })}
        <span
          title={stepNames.join(" -> ")}
          aria-label={funnelText(content, "stepListAriaLabel", "Steps: {steps}", {
            steps: stepNames.join(", "),
          })}
          style={{ marginInlineStart: 6, cursor: "help", textDecoration: "underline dotted", textUnderlineOffset: 2 }}
        >
          {currentStepName}
        </span>
      </p>

      {readinessSurface ? (
        <div
          className="mt-2 flex flex-wrap items-center gap-2"
          data-native-readiness-signal="true"
          data-native-experience-pipeline="EXP-01 EXP-11"
        >
          <span className="inline-flex min-h-7 items-center rounded-full border border-emerald-400/35 bg-emerald-400/10 px-3 text-xs font-bold text-emerald-100">
            {readinessSurface.progressLabel}
          </span>
          <span className="inline-flex min-h-7 min-w-0 items-center rounded-full border border-slate-600/60 bg-slate-900/55 px-3 text-xs font-semibold text-slate-300">
            <span className="shrink-0 text-slate-500" style={{ marginInlineEnd: 4 }}>
              {funnelText(content, "gamificationPreviewReadinessBlockersLabel", "Fix next")}:{" "}
            </span>
            <span className="min-w-0 truncate">{readinessSurface.nextLabel}</span>
          </span>
        </div>
      ) : null}

      {headingLevel === "h1" ? (
        <h1 className="mt-1 text-xl font-bold md:text-2xl">
          {title}
        </h1>
      ) : (
        <h2 className="mt-1 text-xl font-bold md:text-2xl">
          {title}
        </h2>
      )}
      <p className="mt-1 text-sm text-muted-foreground">
        {subtitleKey ? content[subtitleKey] : ""}
      </p>
    </div>
  );
}
